/**
 * MediaCentre Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive media center with advanced features:
 * - Multi-media support (video, audio, images) with streaming capabilities
 * - Advanced filtering system with dynamic options
 * - Pagination with customizable page sizes
 * - Video modal with fullscreen support and platform integration (YouTube, Vimeo)
 * - Audio player integration with local and external URL support
 * - Image modal with zoom functionality
 * - Date range filtering with calendar picker
 * - Search functionality across content
 * - Dynamic thumbnail generation and caching with persistent cache
 * - Series-based navigation and filtering
 * - Responsive grid and list view modes
 * - Smart filter dependency management
 * - URL parameter integration for deep linking
 * 
 * Features:
 * - Real-time media streaming with fallback to blob downloads
 * - YouTube and Vimeo auto-thumbnail generation
 * - Audio context integration for seamless playback
 * - Persistent image blob URL caching that survives navigation
 * - Advanced pagination with smooth transitions
 * - Filter state management with URL synchronization
 * - Media type classification and display
 * - Theme and product categorization
 * - Entity and topic organization
 * 
 * @returns {JSX.Element} MediaCentre component with comprehensive media management
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { useAudio } from '../../contexts/AudioContext';
import apiHelper from '../../utils/apiHelper';
import imageCache from '../../utils/imageCache';
import DatePicker from 'react-datepicker';
import './MediaCentre.css';
import 'react-datepicker/dist/react-datepicker.css';

const { FILE_NAMES } = require('../../utils/constants');

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * MediaCentre component for comprehensive media management and display
 * with advanced filtering, search, and playback capabilities
 */
const MediaCentreComponent = () => {
    const navigate = useNavigate();
    const { series: urlSeries } = useParams();
    
    const audioContext = useAudio();

    const { 
        playAudio
    } = audioContext;
    
    // State management
    const [mediaData, setMediaData] = useState([]);
    const [allMediaData, setAllMediaData] = useState([]); // Cache all data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(12); // Items per page
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [loadingPageNumber, setLoadingPageNumber] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSeries, setSelectedSeries] = useState('all');
    const [selectedTopic, setSelectedTopic] = useState('all');
    const [selectedProduct, setSelectedProduct] = useState('all');
    const [selectedTheme, setSelectedTheme] = useState('all');
    const [selectedEntity, setSelectedEntity] = useState('all');
    const [selectedMediaType, setSelectedMediaType] = useState('all');
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    
    // Video loading states (similar to NewsModal)
    const [videoSource, setVideoSource] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);

    // Image modal states
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    
    // Image loading states
    const [imageUrls, setImageUrls] = useState({}); // Cache for loaded image URLs

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const [areThumbnailsLoading, setAreThumbnailsLoading] = useState(true);

    // Cleanup: Decrement reference counts when component unmounts
    useEffect(() => {
        return () => {
            // Decrement reference counts for all images this component was using
            Object.keys(imageUrls).forEach(imageKey => {
                const cacheKey = `media-image-${imageKey}`;
                imageCache.decrementRefCount(cacheKey);
            });
            
            // Decrement reference counts for all thumbnails this component was using
            Object.keys(videoThumbnails).forEach(videoKey => {
                const cacheKey = `media-thumbnail-${videoKey}`;
                imageCache.decrementRefCount(cacheKey);
            });
        };
    }, [imageUrls, videoThumbnails]);

    // Video loading logic (adapted from NewsModal)
    useEffect(() => {
        const loadVideo = async () => {
            if (loadingRef.current || !selectedVideo?.video) return;

            loadingRef.current = true;
            setIsVideoLoading(true);
            setVideoSource(null);
            
            const video = selectedVideo.video;
            
            if (video.includes('https://')) {
                // Check if it's a YouTube or Vimeo URL that needs iframe embedding
                const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
                const isVimeo = video.includes('vimeo.com');

                if (isYouTube || isVimeo) {
                    // For YouTube/Vimeo, we'll set a flag to use iframe instead of video element
                    setVideoSource(getEmbeddableVideoUrl(video));
                    setIsVideoEmbedded(true);
                } else {
                    // For other HTTPS URLs, use them directly
                    setVideoSource(video);
                    setIsVideoEmbedded(false);
                }

                setIsVideoLoading(false);
                loadingRef.current = false;
            } else {
                // For local videos, use streaming endpoint instead of downloading entire file
                const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
                const encodedVideo = encodeURIComponent(video);
                
                // Create base URL without /api for media endpoint
                const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, '');
                
                // Try both possible endpoints
                const streamingUrls = [
                    `${mediaBaseUrl}/media/stream/${encodedVideo}`,
                    `${apiBaseUrl}/media/stream/${encodedVideo}`
                ];
                
                let videoLoaded = false;
                      
                // Test if the streaming URLs are accessible
                                    for (const streamingUrl of streamingUrls) {
                        try {
                            const testResponse = await fetch(streamingUrl, { 
                                method: 'HEAD'
                                // Removed Range header - we're just checking if file exists
                            });
                            
                            if (testResponse.ok) {
                                setVideoSource(streamingUrl);
                                setIsVideoEmbedded(false);
                                videoLoaded = true;
                                break;
                            }
                        } catch (streamError) {
                            // Try next URL
                        }
                }
                
                if (!videoLoaded) {
                    // Fallback to the old blob method
                    try {
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    } catch (blobError) {
                        // Both streaming and blob download failed
                    }
                }
                
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        if (selectedVideo?.video) {
            loadVideo();
        }

        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [selectedVideo?.video]);

    // Helper function to convert video URLs to embeddable format (same as NewsModal)
    const getEmbeddableVideoUrl = (url) => {
        if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1].split('&')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('vimeo.com/')) {
            const videoId = url.split('vimeo.com/')[1].split('?')[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url; // Return original URL if not YouTube/Vimeo
    };

    // Load data and handle URL parameters
    // Efficient data loading with caching
    const loadAllData = async () => {
        try {
            setLoading(true);
            
            // Fetch all media centre data from server (only once)
            const mediaCentreResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MEDIA_CENTRE);
            const rawData = mediaCentreResult.fileContent;
            
            // Enhance media data 
            const enhancedData = rawData.map((item) => {
                // Parse series - handle both arrays and comma-separated strings
                let seriesArray = [];
                
                if (Array.isArray(item.series)) {
                    // If it's already an array, flatten any comma-separated values
                    seriesArray = item.series.flatMap(s => 
                        typeof s === 'string' ? s.split(',').map(part => part.trim()) : [s]
                    );
                } else if (typeof item.series === 'string') {
                    // If it's a string, split by comma
                    seriesArray = item.series.split(',').map(s => s.trim());
                } else if (item.series) {
                    // Fallback for other types
                    seriesArray = [item.series];
                }
                
                // Remove duplicates and empty values
                const uniqueSeries = [...new Set(seriesArray.filter(s => s && s.trim() !== ''))];
                
                return {
                    ...item,
                    series: uniqueSeries,
                    mediaType: item.mediaType || 'Video' // Default to Video if not specified
                };
            });
            
            setAllMediaData(enhancedData);
            
        } catch (err) {
            setError('Failed to load media content');
        } finally {
            setLoading(false);
        }
    };

    // Efficient pagination using cached data
    const loadMediaData = async (page = 1, resetPage = false) => {
        try {
            if (resetPage) {
                setCurrentPage(1);
                page = 1;
            }
            
            setIsLoadingPage(page !== 1);
            
            // Use cached data instead of re-fetching
            let filteredData = allMediaData.filter(item => {
                const matchesSearch = !searchTerm || 
                    item.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.shortDescription.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesSeries = selectedSeries === 'all' || 
                    (item.series && item.series.includes(selectedSeries));
                const matchesProduct = selectedProduct === 'all' || 
                    item.productService === selectedProduct;
                const matchesTheme = selectedTheme === 'all' || 
                    item.theme === selectedTheme;
                const matchesEntity = selectedEntity === 'all' || 
                    item.entity === selectedEntity;
                const matchesTopic = selectedTopic === 'all' || 
                    item.topic === selectedTopic;
                const matchesMediaType = selectedMediaType === 'all' || 
                    item.mediaType.toLowerCase() === selectedMediaType;
                
                // Date range filtering
                let matchesDateFrom = true;
                let matchesDateTo = true;
                if (dateFrom || dateTo) {
                    const itemDate = new Date(item.publishDate);
                    matchesDateFrom = !dateFrom || itemDate >= new Date(dateFrom);
                    matchesDateTo = !dateTo || itemDate <= new Date(dateTo);
                }

                return matchesSearch && matchesSeries && matchesProduct && matchesTheme && 
                       matchesEntity && matchesTopic && matchesMediaType && matchesDateFrom && matchesDateTo;
            });
            
            // Sort by publishDate, newest first
            filteredData.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
            
            // Calculate pagination
            const totalCount = filteredData.length;
            const totalPagesCount = Math.ceil(totalCount / pageSize);
            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const pageData = filteredData.slice(startIndex, endIndex);
            
            // Fetch Vimeo thumbnails only for items on current page (async, don't block UI)
            const pageDataWithThumbs = await Promise.all(pageData.map(async (item) => {
                if (item.webURLLink && item.webURLLink.includes('vimeo.com') && item.mediaType.toLowerCase() === 'video') {
                    try {
                        const vimeoThumb = await getVimeoThumbnail(item.webURLLink);
                        return {
                            ...item,
                            vimeoThumbnail: vimeoThumb
                        };
                    } catch (err) {
                        console.error('Error fetching Vimeo thumbnail:', err);
                        return item;
                    }
                }
                return item;
            }));
            
            
            // Update state
            setMediaData(pageDataWithThumbs);
            setTotalItems(totalCount);
            setTotalPages(totalPagesCount);
            setCurrentPage(page);
            
        } catch (err) {
            setError('Failed to process media content');
            console.error('Error processing media content:', err);
        } finally {
            setIsLoadingPage(false);
        }
    };

    // Pagination handlers
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
            setLoadingPageNumber(newPage);
            loadMediaData(newPage);
            // Scroll to top of content
            document.querySelector('.content-section')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setLoadingPageNumber(1);
        loadMediaData(1, true); // Reset to page 1 with new page size
    };

    // Initial data load - load all data once on mount
    useEffect(() => {
        loadAllData();
    }, []);

    // When all data is loaded, apply filters and show first page
    useEffect(() => {
        if (allMediaData.length > 0) {
            loadMediaData(1, true);
        }
    }, [allMediaData]);

    useEffect(() => {
        // Handle URL parameters
        if (urlSeries) {
            setSelectedSeries(decodeURIComponent(urlSeries));
        }
    }, [urlSeries]);

    // Reload data when filters change (debounced for performance)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (allMediaData.length > 0) { // Only process if we have cached data
                loadMediaData(1, true);
            }
        }, 150); // Reduced debounce for snappier filtering

        return () => clearTimeout(timeoutId);
    }, [searchTerm, selectedSeries, selectedProduct, selectedTheme, selectedEntity, selectedTopic, selectedMediaType, dateFrom, dateTo, pageSize, allMediaData]);

    // Date formatting and handling functions
    const formatDate = (date) => {
        if (!date) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleApplyDates = () => {
        setDateFrom(tempStartDate);
        setDateTo(tempEndDate);
        setShowDatePicker(false);
    };

    // Get available years and months from media data
    const getAvailableYearsAndMonths = (data) => {
        const yearsAndMonths = new Map();
        
        data.forEach(item => {
            const date = new Date(item.publishDate);
            if (date && !isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = date.getMonth();
                
                if (!yearsAndMonths.has(year)) {
                    yearsAndMonths.set(year, new Set());
                }
                yearsAndMonths.get(year).add(month);
            }
        });
        
        return yearsAndMonths;
    };

    const availableYearsAndMonths = useMemo(() => getAvailableYearsAndMonths(allMediaData), [allMediaData]);

    // Dynamic filter options based on current selections
    const getDynamicFilterOptions = useMemo(() => {
       
        
        // For Series: filter by everything EXCEPT series selection
        let seriesData = [...allMediaData];
        if (selectedTopic !== 'all') {
            seriesData = seriesData.filter(item => item.topic && item.topic.toLowerCase().includes(selectedTopic.toLowerCase()));
        }
        if (selectedProduct !== 'all') {
            seriesData = seriesData.filter(item => item.productService === selectedProduct);
        }
        if (selectedTheme !== 'all') {
            seriesData = seriesData.filter(item => item.theme === selectedTheme);
        }
        if (selectedEntity !== 'all') {
            seriesData = seriesData.filter(item => item.entity === selectedEntity);
        }
        
        // For Topics: filter by everything EXCEPT topic selection
        let topicsData = [...allMediaData];
        if (selectedSeries !== 'all') {
            topicsData = topicsData.filter(item => item.series.includes(selectedSeries));
        }
        if (selectedProduct !== 'all') {
            topicsData = topicsData.filter(item => item.productService === selectedProduct);
        }
        if (selectedTheme !== 'all') {
            topicsData = topicsData.filter(item => item.theme === selectedTheme);
        }
        if (selectedEntity !== 'all') {
            topicsData = topicsData.filter(item => item.entity === selectedEntity);
        }
        
        // For Products: filter by everything EXCEPT product selection
        let productsData = [...allMediaData];
        if (selectedSeries !== 'all') {
            productsData = productsData.filter(item => item.series.includes(selectedSeries));
        }
        if (selectedTopic !== 'all') {
            productsData = productsData.filter(item => item.topic && item.topic.toLowerCase().includes(selectedTopic.toLowerCase()));
        }
        if (selectedTheme !== 'all') {
            productsData = productsData.filter(item => item.theme === selectedTheme);
        }
        if (selectedEntity !== 'all') {
            productsData = productsData.filter(item => item.entity === selectedEntity);
        }
        
        // For Themes: filter by everything INCLUDING product selection
        let themesData = [...allMediaData];
        if (selectedSeries !== 'all') {
            themesData = themesData.filter(item => item.series.includes(selectedSeries));
        }
        if (selectedTopic !== 'all') {
            themesData = themesData.filter(item => item.topic && item.topic.toLowerCase().includes(selectedTopic.toLowerCase()));
        }
        if (selectedProduct !== 'all') {
            themesData = themesData.filter(item => item.productService === selectedProduct);
        }
        if (selectedEntity !== 'all') {
            themesData = themesData.filter(item => item.entity === selectedEntity);
        }
        
        // For Entities: filter by everything INCLUDING product selection
        let entitiesData = [...allMediaData];
        if (selectedSeries !== 'all') {
            entitiesData = entitiesData.filter(item => item.series.includes(selectedSeries));
        }
        if (selectedTopic !== 'all') {
            entitiesData = entitiesData.filter(item => item.topic && item.topic.toLowerCase().includes(selectedTopic.toLowerCase()));
        }
        if (selectedProduct !== 'all') {
            entitiesData = entitiesData.filter(item => item.productService === selectedProduct);
        }
        if (selectedTheme !== 'all') {
            entitiesData = entitiesData.filter(item => item.theme === selectedTheme);
        }
        
        // Extract unique values for each filter
        const dynamicOptions = {
            series: [...new Set(seriesData.flatMap(item => (item.series || []).filter(series => series && series.trim() !== '')))].sort(),
            topics: [...new Set(topicsData.map(item => item.topic))].filter(Boolean).sort(),
            products: [...new Set(productsData.map(item => item.productService))].filter(Boolean).sort(), // Excludes own selection
            themes: [...new Set(themesData.map(item => item.theme))].filter(Boolean).sort(),
            entities: [...new Set(entitiesData.map(item => item.entity))].filter(Boolean).sort()
        };
        
        // Always include currently selected options (so they don't disappear from dropdowns)
        if (selectedSeries !== 'all' && !dynamicOptions.series.some(series => series === selectedSeries)) {
            dynamicOptions.series.push(selectedSeries);
            dynamicOptions.series.sort();
        }
        if (selectedTopic !== 'all' && !dynamicOptions.topics.includes(selectedTopic)) {
            dynamicOptions.topics.push(selectedTopic);
            dynamicOptions.topics.sort();
        }
        if (selectedProduct !== 'all' && !dynamicOptions.products.includes(selectedProduct)) {
            dynamicOptions.products.push(selectedProduct);
            dynamicOptions.products.sort();
        }
        if (selectedTheme !== 'all' && !dynamicOptions.themes.includes(selectedTheme)) {
            dynamicOptions.themes.push(selectedTheme);
            dynamicOptions.themes.sort();
        }
        if (selectedEntity !== 'all' && !dynamicOptions.entities.includes(selectedEntity)) {
            dynamicOptions.entities.push(selectedEntity);
            dynamicOptions.entities.sort();
        }
        
        
        return dynamicOptions;
    }, [allMediaData, selectedSeries, selectedTopic, selectedProduct, selectedTheme, selectedEntity]);
    
    // Get filter options for each step (excluding the current filter being changed)
    const getFilterOptionsForStep = (excludeFilter) => {
        let workingData = [...allMediaData];
        
        // Apply all filters except the one being changed
        if (excludeFilter !== 'series' && selectedSeries !== 'all') {
            workingData = workingData.filter(item => item.series.includes(selectedSeries));
        }
        if (excludeFilter !== 'topic' && selectedTopic !== 'all') {
            workingData = workingData.filter(item => item.topic && item.topic.toLowerCase().includes(selectedTopic.toLowerCase()));
        }
        if (excludeFilter !== 'product' && selectedProduct !== 'all') {
            workingData = workingData.filter(item => item.productService === selectedProduct);
        }
        if (excludeFilter !== 'theme' && selectedTheme !== 'all') {
            workingData = workingData.filter(item => item.theme === selectedTheme);
        }
        if (excludeFilter !== 'entity' && selectedEntity !== 'all') {
            workingData = workingData.filter(item => item.entity === selectedEntity);
        }
        
        return {
            series: [...new Set(allMediaData.flatMap(item => (item.series || []).filter(series => series && series.trim() !== '')))].sort(),
            topics: [...new Set(workingData.map(item => item.topic))].filter(Boolean).sort(),
            products: [...new Set(workingData.map(item => item.productService))].filter(Boolean).sort(),
            themes: [...new Set(workingData.map(item => item.theme))].filter(Boolean).sort(),
            entities: [...new Set(workingData.map(item => item.entity))].filter(Boolean).sort()
        };
    };

    // Get applied filters for tag display (similar to ETFBookBrowser)
    const getAppliedFilters = () => {
        const appliedFilters = [];
        
        if (searchTerm) {
            appliedFilters.push({
                type: 'search',
                label: 'Search',
                value: searchTerm,
                onRemove: () => setSearchTerm('')
            });
        }
        
        if (selectedSeries !== 'all') {
            appliedFilters.push({
                type: 'series',
                label: 'Series',
                value: selectedSeries,
                onRemove: () => setSelectedSeries('all')
            });
        }
        
        if (selectedTopic !== 'all') {
            appliedFilters.push({
                type: 'topic',
                label: 'Topic',
                value: selectedTopic,
                onRemove: () => setSelectedTopic('all')
            });
        }
        
        if (selectedProduct !== 'all') {
            appliedFilters.push({
                type: 'product',
                label: 'Product',
                value: selectedProduct,
                onRemove: () => setSelectedProduct('all')
            });
        }
        
        if (selectedTheme !== 'all') {
            appliedFilters.push({
                type: 'theme',
                label: 'Theme',
                value: selectedTheme,
                onRemove: () => setSelectedTheme('all')
            });
        }
        
        if (selectedEntity !== 'all') {
            appliedFilters.push({
                type: 'entity',
                label: 'Entity',
                value: selectedEntity,
                onRemove: () => setSelectedEntity('all')
            });
        }
        
        if (selectedMediaType !== 'all') {
            appliedFilters.push({
                type: 'mediaType',
                label: 'Media Type',
                value: selectedMediaType.charAt(0).toUpperCase() + selectedMediaType.slice(1),
                onRemove: () => setSelectedMediaType('all')
            });
        }
        
        if (dateFrom || dateTo) {
            const dateRangeValue = dateFrom && dateTo 
                ? `${formatDate(dateFrom)} - ${formatDate(dateTo)}`
                : dateFrom 
                ? `From ${formatDate(dateFrom)}`
                : `Until ${formatDate(dateTo)}`;
            
            appliedFilters.push({
                type: 'dateRange',
                label: 'Date Range',
                value: dateRangeValue,
                onRemove: () => {
                    setDateFrom(null);
                    setDateTo(null);
                }
            });
        }
        
        return appliedFilters;
    };

    // Media data is now filtered and paginated in loadMediaData function

    useEffect(() => {
        if (mediaData.length > 0) {
           
            fetchThumbnailsForMedia();
            loadImagesFromBlob();
        }
    }, [mediaData]);

    // Generate series data dynamically from JSON data
    const seriesData = React.useMemo(() => {
        const allSeries = [...new Set(mediaData.flatMap(item => 
            (item.series || []).filter(series => series && series.trim() !== '')
        ))];
        
        const seriesMap = {};
        allSeries.forEach(seriesName => {
            seriesMap[seriesName] = {
                count: mediaData.filter(item => 
                    item.series && item.series.includes(seriesName)
                ).length
            };
        });
        
        return seriesMap;
    }, [mediaData]);

    // Simple YouTube thumbnail fetcher
    const getYouTubeThumbnail = (youtubeUrl) => {
        try {
            const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
            if (!videoId) {
                
                return null;
            }
            
           
            // Use maxresdefault for best quality, fallback to hqdefault
            const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
         
            return thumbnailUrl;
        } catch (err) {
            console.error('Error fetching YouTube thumbnail:', err);
            return null;
        }
    };

    const getVimeoThumbnail = async (vimeoUrl) => {
        try {
            const videoId = vimeoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
            if (!videoId) {
                return null;
            }
            
  
            
            // Method 1: Try oEmbed API (modern approach)
            try {
                const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}&width=640`;
                const oembedResponse = await fetch(oembedUrl);
                
                if (!oembedResponse.ok) {
                    if (oembedResponse.status === 404) {
                        console.warn(`Vimeo video ${videoId} not found via oEmbed. Video may have been deleted or made private.`);
                    }
                    throw new Error(`oEmbed API returned ${oembedResponse.status}`);
                }
                
                const oembedData = await oembedResponse.json();
                
                if (oembedData.thumbnail_url) {
                    return oembedData.thumbnail_url;
                }
            } catch (oembedError) {
                // oEmbed failed, continue to CDN fallback
                console.warn(`oEmbed failed for video ${videoId}:`, oembedError.message);
            }
            
            // Method 3: Try direct CDN approach
            try {
               
                const cdnUrl = `https://i.vimeocdn.com/video/${videoId}_640x360.jpg`;
            
                // Test if the URL actually works
                const testResponse = await fetch(cdnUrl, { method: 'HEAD' });
                if (testResponse.ok) {
                    return cdnUrl;
                } 
            } catch (cdnError) {
                // CDN approach failed
            }
            
            return null;
            
        } catch (err) {
            console.error('Error fetching Vimeo thumbnail:', err);
            return null;
        }
    };

    // Image click handler
    const handleImageClick = (mediaItem, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        
        setSelectedImage(mediaItem);
        setShowImageModal(true);
    };

    // ESC key handler for modal closing
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                if (showImageModal) {
                    setShowImageModal(false);
                }
                if (showVideoModal) {
                    closeVideoModal();
                }
            }
        };

        // Add event listener when modals are open
        if (showImageModal || showVideoModal) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showImageModal, showVideoModal]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            // Clean up image blob URLs
            Object.values(imageUrls).forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            
            // Clean up video thumbnail blob URLs
            Object.values(videoThumbnails).forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    // Enhanced media click handler (supports video, audio, and images)
    const handleMediaClick = async (mediaItem, event) => {
        // Handle image files
        if (mediaItem.mediaType.toLowerCase() === 'image') {
            handleImageClick(mediaItem, event);
            return;
        }
        
        // Handle audio files
        if (mediaItem.mediaType.toLowerCase() === 'audio' || mediaItem.mediaType === 'Audio') {
            if (!playAudio) {
                alert('Audio player is not available. Please refresh the page.');
                return;
            }
            
            try {
                // Capture cursor position for modal positioning
                let mousePosition = null;
                if (event) {
                    mousePosition = {
                        x: event.clientX,
                        y: event.clientY,
                        scrollY: window.scrollY
                    };
                }
                
                // Check if it's an external audio URL (Spotify, SoundCloud, etc.)
                if (mediaItem.webURLLink && (
                    mediaItem.webURLLink.includes('spotify.com') ||
                    mediaItem.webURLLink.includes('soundcloud.com') ||
                    mediaItem.webURLLink.includes('anchor.fm') ||
                    mediaItem.webURLLink.includes('pod.link') ||
                    mediaItem.webURLLink.includes('goloudplayer.com') ||
                    mediaItem.webURLLink.includes('https://') ||
                    mediaItem.webURLLink.includes('http://')
                )) {
                    // External URL - pass directly to AudioContext
                    playAudio(mediaItem, mousePosition);
                    return;
                }
                
                // Local blob audio file - need to load it first
                
                // Extract filename from various URL patterns (similar to video handling)
                let audioFilename = mediaItem.webURLLink;
                
                // If it's a full URL, extract just the filename
                if (mediaItem.webURLLink.includes('/media/stream/')) {
                    audioFilename = mediaItem.webURLLink.split('/media/stream/')[1];
                } else if (mediaItem.webURLLink.includes('/')) {
                    // If it has slashes but not HTTP, take the last part
                    audioFilename = mediaItem.webURLLink.split('/').pop();
                }
                // If it's already just a filename (no slashes), use as-is
                
                // Try streaming endpoint first, then fallback to blob download
                let audioUrl = null;
                
                try {
                    // Try streaming endpoint first (like video handling)
                    const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
                    const encodedAudioFilename = encodeURIComponent(audioFilename);
                    
                    // Create base URL without /api for media endpoint
                    const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, '');
                    
                    // Try both possible endpoints
                    const streamingUrls = [
                        `${mediaBaseUrl}/media/stream/${encodedAudioFilename}`,
                        `${apiBaseUrl}/media/stream/${encodedAudioFilename}`
                    ];
                    
                    let audioLoaded = false;
                    
                    for (const streamingUrl of streamingUrls) {
                        try {
                            const testResponse = await fetch(streamingUrl, { 
                                method: 'HEAD'
                                // Removed Range header - we're just checking if file exists
                            });
                            
                            if (testResponse.ok) {
                                audioUrl = streamingUrl;
                                audioLoaded = true;
                                break;
                            }
                        } catch (urlError) {
                            // Try next URL
                        }
                    }
                    
                    if (!audioLoaded) {
                        throw new Error('All streaming URLs failed');
                    }
                } catch (streamError) {
                    try {
                        // Fallback to blob download
                        const { fileContent } = await apiHelper.fetchAudioByName(audioFilename);
                        audioUrl = URL.createObjectURL(fileContent);
                    } catch (blobError) {
                        alert('Failed to load audio file. Please try again.');
                        return;
                    }
                }
                
                // Create modified media item with the loaded audio URL
                const audioMediaItem = {
                    ...mediaItem,
                    webURLLink: audioUrl,
                    originalWebURLLink: mediaItem.webURLLink, // Keep original for reference
                    isLocalBlob: !audioUrl.startsWith('http') // Flag to help cleanup later
                };
                
                playAudio(audioMediaItem, mousePosition);
                
            } catch (error) {
                alert('Error starting audio: ' + error.message);
            }
            return;
        }
        
        // Handle video files
        if (mediaItem.mediaType.toLowerCase() === 'video' || mediaItem.mediaType === 'Video') {
            // Check if it's a Vimeo or YouTube URL
            if (mediaItem.webURLLink.includes('vimeo.com') || 
                mediaItem.webURLLink.includes('youtube.com') || 
                mediaItem.webURLLink.includes('youtu.be')) {
                
                // Capture mouse position for video modal positioning
                let mousePosition = null;
                if (event) {
                    mousePosition = {
                        x: event.clientX,
                        y: event.clientY,
                        scrollY: window.scrollY
                    };
                }
                
                setSelectedVideo(mediaItem);
                setShowVideoModal(true);
                
                // Apply viewport centering positioning - ONLY for video modals
                if (mousePosition) {
                    setTimeout(() => {
                        // Use specific selectors for video modals only
                        const videoModal = document.querySelector('.video-modal');
                        const videoOverlay = document.querySelector('.video-modal-overlay');
                        
                        if (videoModal && videoOverlay) {
                            // Position modal in center of current viewport
                            const viewportCenterX = window.innerWidth / 2;
                            const viewportCenterY = window.innerHeight / 2;
                            
                            // Apply positioning ONLY to video modals
                            videoOverlay.style.alignItems = 'center';
                            videoOverlay.style.justifyContent = 'center';
                            videoOverlay.style.padding = '0';
                            
                            // Use fixed positioning to center in viewport
                            videoModal.style.position = 'fixed';
                            videoModal.style.top = '50%';
                            videoModal.style.left = '50%';
                            videoModal.style.transform = 'translate(-50%, -50%)';
                        }
                    }, 150);
                }
                return;
            }
            
            // Handle local video files (from blob storage)
            
            // Extract filename from various URL patterns
            let videoFilename = mediaItem.webURLLink;
            
            // If it's a full URL, extract just the filename
            if (mediaItem.webURLLink.includes('/media/stream/')) {
                videoFilename = mediaItem.webURLLink.split('/media/stream/')[1];
            } else if (mediaItem.webURLLink.includes('http://') || mediaItem.webURLLink.includes('https://')) {
                // If it's a full HTTP URL, extract filename from the end
                videoFilename = mediaItem.webURLLink.split('/').pop();
            } else if (mediaItem.webURLLink.includes('/')) {
                // If it has slashes but not HTTP, take the last part
                videoFilename = mediaItem.webURLLink.split('/').pop();
            }
            // If it's already just a filename (no slashes), use as-is
            
            // Create a modified mediaItem with the correct video reference
            const videoMediaItem = {
                ...mediaItem,
                video: videoFilename // Use the same field name as News articles
            };
            
            // Capture mouse position for video modal positioning
            let mousePosition = null;
            if (event) {
                mousePosition = {
                    x: event.clientX,
                    y: event.clientY,
                    scrollY: window.scrollY
                };
            }
            
            setSelectedVideo(videoMediaItem);
            setShowVideoModal(true);
            
            // Apply viewport centering positioning
            if (mousePosition) {
                setTimeout(() => {
                    const videoModal = document.querySelector('.video-modal');
                    const videoOverlay = document.querySelector('.video-modal-overlay');
                    
                    if (videoModal && videoOverlay) {
                        videoOverlay.style.alignItems = 'center';
                        videoOverlay.style.justifyContent = 'center';
                        videoOverlay.style.padding = '0';
                        
                        videoModal.style.position = 'fixed';
                        videoModal.style.top = '50%';
                        videoModal.style.left = '50%';
                        videoModal.style.transform = 'translate(-50%, -50%)';
                    }
                }, 150);
            }
            return;
        }
        
        // Handle other media types (images, documents, etc.)
        if (mediaItem.mediaType.toLowerCase() === 'image') {
            // For now, open in new tab - we'll implement image modal later
            window.open(mediaItem.webURLLink, '_blank');
            return;
        }
        
        // Default fallback
        window.open(mediaItem.webURLLink, '_blank');
    };

    // Backward compatibility - keep the old function name
    const handleVideoClick = handleMediaClick;

    // Close video modal
    const closeVideoModal = () => {
        setShowVideoModal(false);
        setSelectedVideo(null);
        
        // Clean up video state
        setVideoSource(null);
        setIsVideoLoading(true);
        setIsVideoEmbedded(false);
        
        // Clean up blob URLs
        if (currentBlobUrl.current) {
            URL.revokeObjectURL(currentBlobUrl.current);
            currentBlobUrl.current = null;
        }
        
        loadingRef.current = false;
    };

    // Toggle fullscreen for video modal
    const toggleVideoFullscreen = () => {
        // Use specific selector for video modals only
        const videoModal = document.querySelector('.video-modal');
        
        if (!document.fullscreenElement) {
            // Enter fullscreen
            if (videoModal.requestFullscreen) {
                videoModal.requestFullscreen();
            } else if (videoModal.webkitRequestFullscreen) { // Safari
                videoModal.webkitRequestFullscreen();
            } else if (videoModal.msRequestFullscreen) { // IE/Edge
                videoModal.msRequestFullscreen();
            }
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { // Safari
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { // IE/Edge
                document.msExitFullscreen();
            }
        }
    };



    // Get Vimeo embed URL
    const getVimeoEmbedUrl = (vimeoUrl) => {
        // Match both regular and private video URLs with privacy hash
        const match = vimeoUrl.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
        if (!match) return null;
        
        const videoId = match[1];
        const privacyHash = match[2]; // Will be undefined for public videos
        
        // Build embed URL with privacy hash as parameter if present
        let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
        
        if (privacyHash) {
            embedUrl += `&h=${privacyHash}`;
        }
        
        return embedUrl;
    };

    // Handle series click
    const handleSeriesClick = (seriesName) => {
        setSelectedSeries(seriesName);
        navigate(`/MediaCentre/series/${encodeURIComponent(seriesName)}`);
    };

    // Handle reset filters
    const handleResetFilters = () => {
        setSearchTerm('');
        setSelectedSeries('all');
        setSelectedProduct('all');
        setSelectedTheme('all');
        setSelectedEntity('all');
        setSelectedTopic('all');
        setSelectedMediaType('all');
        setDateFrom(null);
        setDateTo(null);
        setShowDatePicker(false);
        setTempStartDate(null);
        setTempEndDate(null);
        navigate('/MediaCentre');
    };

    // Check if any filters are active
    const hasActiveFilters = searchTerm !== '' || selectedSeries !== 'all' || 
                            selectedProduct !== 'all' || selectedTheme !== 'all' || 
                            selectedEntity !== 'all' || selectedTopic !== 'all' ||
                            selectedMediaType !== 'all' ||
                            dateFrom !== null || dateTo !== null;

    // Get theme color class
    const getThemeColor = (theme) => {
        const colorMap = {
            'Tax': 'cornflower',
            'ESG': 'mint',
            'Financial Literacy': 'gold',
            'Digitalisation': 'coral',
            'Product': 'dust-rose',
            'Legal Structures': 'cyan',
            'Innovation': 'peach',
            'Strategy': 'sage'
        };
        return colorMap[theme] || 'light-gray';
    };

    // Get product color class
    const getProductColor = (product) => {
        const colorMap = {
            'ETFs': 'cornflower',
            'Hedge Fund Strategies': 'mint',
            'Traditional Fund Strategies': 'gold',
            'Digital Assets': 'coral',
            'Private Asset Strategies': 'sage',
            'Management Companies': 'dark-red',
            'Industry Competitiveness': 'blue'
        };
        return colorMap[product] || 'light-gray';
    };

    // Get media type icon
    const getMediaTypeIcon = (mediaType) => {
        switch (mediaType) {
            case 'video': return 'fas fa-play';
            case 'audio': return 'fas fa-headphones';
            case 'image': return 'fas fa-image';
            default: return 'fas fa-file';
        }
    };

    // Get media type display name
    const getMediaTypeDisplay = (mediaType) => {
        switch (mediaType.toLowerCase()) {
            case 'video': return 'Video';
            case 'audio': return 'Audio';
            case 'image': return 'Image';
            default: return 'Media';
        }
    };

    // Check if media type should show duration
    const showDuration = (mediaType) => {
        return mediaType.toLowerCase() === 'video' || mediaType.toLowerCase() === 'audio' || mediaType === 'Audio' || mediaType === 'Video';
    };

    // Check if media type should show play overlay
    const showPlayOverlay = (mediaType) => {
        return mediaType.toLowerCase() === 'video' || mediaType.toLowerCase() === 'audio' || mediaType === 'Audio' || mediaType === 'Video';
    };

    // Load images from blob storage
    const loadImagesFromBlob = async () => {
        const imageUrlsMap = {};
        
        // Process all image media items
        const imageItems = mediaData.filter(item => 
            item.mediaType.toLowerCase() === 'image' && item.webURLLink
        );
        
        for (const item of imageItems) {
            const imageKey = `media-image-${item.webURLLink}`;
            
            try {
                // Check persistent cache first
                const cachedImage = imageCache.getCachedImage(imageKey);
                if (cachedImage) {
                    imageUrlsMap[item.webURLLink] = cachedImage;
                    continue;
                }

                // Define loading function for this image
                const loadImageFunction = async () => {
                    // Check if it's a local file (not external URL)
                    if (!item.webURLLink.includes('http://') && !item.webURLLink.includes('https://')) {
                        
                        try {
                            // Build proper streaming URL - ensure correct path construction
                            const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
                            const encodedFilename = encodeURIComponent(item.webURLLink);
                            
                            // Create base URL without /api for media endpoint
                            const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, '');
                            
                            // Try both possible endpoints
                            const streamingUrls = [
                                `${mediaBaseUrl}/media/stream/${encodedFilename}`,
                                `${apiBaseUrl}/media/stream/${encodedFilename}`
                            ];
                            
                            for (const streamingUrl of streamingUrls) {
                                try {
                                    const testResponse = await fetch(streamingUrl, { 
                                        method: 'HEAD'
                                    });
                                    
                                    if (testResponse.ok) {
                                        return streamingUrl;
                                    }
                                } catch (streamError) {
                                    // Try next URL
                                }
                            }
                            
                            throw new Error('All streaming URLs failed');
                        } catch (error) {
                            // Fallback to blob download
                            const { fileContent } = await apiHelper.fetchImageByName(item.webURLLink);
                            return URL.createObjectURL(fileContent);
                        }
                    } else {
                        // External URL - use directly
                        return item.webURLLink;
                    }
                };

                // Load through persistent cache
                const imageUrl = await imageCache.getImage(imageKey, loadImageFunction);
                imageUrlsMap[item.webURLLink] = imageUrl;
                
            } catch (error) {
                console.error('Error loading image:', item.webURLLink, error);
                // Error processing image - skip it
            }
        }
        
        setImageUrls(imageUrlsMap);
    };

    const fetchThumbnailsForMedia = async () => {
        setAreThumbnailsLoading(true);
        
        const mediaThumbnailsMap = {};
        
        // Process all videos and audio from mediaData
        const mediaItems = mediaData.filter(item => 
            (item.mediaType.toLowerCase() === 'video' || item.mediaType.toLowerCase() === 'audio' || item.mediaType === 'Audio') && item.webURLLink
        );
        
        for (const item of mediaItems) {
            const thumbnailKey = `media-thumbnail-${item.webURLLink}`;
            
            try {
                // Check persistent cache first
                const cachedThumbnail = imageCache.getCachedImage(thumbnailKey);
                if (cachedThumbnail) {
                    mediaThumbnailsMap[item.webURLLink] = cachedThumbnail;
                    continue;
                }

                let thumbnailUrl = null;

                // Priority 1: Use custom thumbnail if available (for all media types, including YouTube/Vimeo)
                if (item.thumbnail && item.thumbnail.trim() !== '' && item.thumbnail !== 'NONE') {
                    
                    // Define loading function for custom thumbnail
                    const loadThumbnailFunction = async () => {
                        // Check if it's a local file (not external URL)
                        if (!item.thumbnail.includes('http://') && !item.thumbnail.includes('https://')) {
                            try {
                                // Build proper streaming URLs - avoid double /api/ paths
                                const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
                                const encodedThumbnail = encodeURIComponent(item.thumbnail);
                                
                                // Create base URL without /api for media endpoint
                                const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, '');
                                
                                const streamingUrls = [
                                    `${mediaBaseUrl}/media/stream/${encodedThumbnail}`,
                                    `${apiBaseUrl}/media/stream/${encodedThumbnail}`
                                ];
                                
                                for (const streamingUrl of streamingUrls) {
                                    try {
                                        const testResponse = await fetch(streamingUrl, { 
                                            method: 'HEAD'
                                        });
                                        
                                        if (testResponse.ok) {
                                            return streamingUrl;
                                        }
                                    } catch (streamError) {
                                        // Streaming URL error, try next
                                    }
                                }
                                
                                // Fallback to blob download
                                const { fileContent } = await apiHelper.fetchVideoByName(item.thumbnail);
                                return URL.createObjectURL(fileContent);
                            } catch (error) {
                                throw new Error('Failed to fetch custom thumbnail');
                            }
                        } else {
                            // External URL thumbnail
                            return item.thumbnail;
                        }
                    };

                    // Load through persistent cache
                    thumbnailUrl = await imageCache.getImage(thumbnailKey, loadThumbnailFunction);
                }
                
                // Priority 2: External video thumbnails (YouTube/Vimeo) - ONLY use platform thumbnails when no custom thumbnail exists
                if (!thumbnailUrl) {
                    const isYouTubeOrVimeo = item.webURLLink.includes('vimeo.com') || item.webURLLink.includes('youtube.com') || item.webURLLink.includes('youtu.be');
                    const shouldUsePlatformThumbnail = item.mediaType.toLowerCase() === 'video' && isYouTubeOrVimeo;
                    
                    if (shouldUsePlatformThumbnail) {
                        // Define loading function for platform thumbnails
                        const loadPlatformThumbnailFunction = async () => {
                            // Generate auto thumbnail from video platform URL
                            if (item.webURLLink.includes('youtube.com') || item.webURLLink.includes('youtu.be')) {
                                return getYouTubeThumbnail(item.webURLLink);
                            } 
                            else if (item.webURLLink.includes('vimeo.com')) {
                                return await getVimeoThumbnail(item.webURLLink);
                            }
                            throw new Error('Unsupported platform');
                        };

                        // Load through persistent cache
                        try {
                            thumbnailUrl = await imageCache.getImage(thumbnailKey, loadPlatformThumbnailFunction);
                        } catch (error) {
                            console.error('Error loading platform thumbnail:', error);
                        }
                    }
                }
                
                // Store the result
                if (thumbnailUrl) {
                    mediaThumbnailsMap[item.webURLLink] = thumbnailUrl;
                }
                
            } catch (error) {
                console.error('Error processing thumbnail for media:', item.webURLLink, error);
                // Error processing thumbnail for media - skip it
            }
        }
       
        setVideoThumbnails(mediaThumbnailsMap);
        setAreThumbnailsLoading(false);
    };

    // Smart filter change handler with automatic dependent filter reset
    const handleSmartFilterChange = (filterType, value) => {
       
        
        // Get what options would be available after this change
        const testData = [...mediaData];
        let filteredData = testData;
        
        // Apply the new filter value
        if (filterType === 'series' && value !== 'all') {
            filteredData = filteredData.filter(item => item.series.includes(value));
        } else if (selectedSeries !== 'all' && filterType !== 'series') {
            filteredData = filteredData.filter(item => item.series.includes(selectedSeries));
        }
        
        if (filterType === 'topic' && value !== 'all') {
            filteredData = filteredData.filter(item => item.topic && item.topic.toLowerCase().includes(value.toLowerCase()));
        } else if (selectedTopic !== 'all' && filterType !== 'topic') {
            filteredData = filteredData.filter(item => item.topic && item.topic.toLowerCase().includes(selectedTopic.toLowerCase()));
        }
        
        if (filterType === 'product' && value !== 'all') {
            filteredData = filteredData.filter(item => item.productService === value);
        } else if (selectedProduct !== 'all' && filterType !== 'product') {
            filteredData = filteredData.filter(item => item.productService === selectedProduct);
        }
        
        if (filterType === 'theme' && value !== 'all') {
            filteredData = filteredData.filter(item => item.theme === value);
        } else if (selectedTheme !== 'all' && filterType !== 'theme') {
            filteredData = filteredData.filter(item => item.theme === selectedTheme);
        }
        
        if (filterType === 'entity' && value !== 'all') {
            filteredData = filteredData.filter(item => item.entity === value);
        } else if (selectedEntity !== 'all' && filterType !== 'entity') {
            filteredData = filteredData.filter(item => item.entity === selectedEntity);
        }
        
        // Get available options after this filter change
        const availableOptions = {
            topics: [...new Set(filteredData.map(item => item.topic))],
            products: [...new Set(filteredData.map(item => item.productService))].filter(Boolean),
            themes: [...new Set(filteredData.map(item => item.theme))].filter(Boolean),
            entities: [...new Set(filteredData.map(item => item.entity))].filter(Boolean)
        };
        
    
        
        // Apply the main filter change
        if (filterType === 'series') {
            setSelectedSeries(value);
            // No navigation - just filter in place
        } else if (filterType === 'topic') {
            setSelectedTopic(value);
        } else if (filterType === 'product') {
            setSelectedProduct(value);
        } else if (filterType === 'theme') {
            setSelectedTheme(value);
        } else if (filterType === 'entity') {
            setSelectedEntity(value);
        }
        
        // Reset dependent filters if their current value is no longer available
        // NOTE: Topics is NOT reset because it's a "result" filter, not a "cause" filter
        
        if (filterType !== 'product' && selectedProduct !== 'all' && !availableOptions.products.includes(selectedProduct)) {
         
            setSelectedProduct('all');
        }
        
        if (filterType !== 'theme' && selectedTheme !== 'all' && !availableOptions.themes.includes(selectedTheme)) {
            
            setSelectedTheme('all');
        }
        
        if (filterType !== 'entity' && selectedEntity !== 'all' && !availableOptions.entities.includes(selectedEntity)) {
            setSelectedEntity('all');
        }
    };

    if (loading) {
        return (
            <div className="media-centre-container">
                <div className="container mt-6">
                    <LoadingSpinner text="Loading Media Centre..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="media-centre-container">
                <div className="container mt-6">
                    <div className="error-container">
                        <h2>Failed to Load Media Centre</h2>
                        <p>{error}</p>
                        <button className="btn btn-primary" onClick={() => window.location.reload()}>
                            <i className="fas fa-redo"></i> Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="media-centre-container">
            {/* Hero Section */}
            {/* <div className="media-centre-hero">
                <div className="container">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            <i className="fas fa-play-circle"></i>
                            Irish Funds Media Centre
                        </h1>
                        <p className="hero-subtitle">
                            Your gateway to expert insights, educational content, and industry leadership
                        </p>
                    </div>
                </div>
            </div> */}

            {/* Search and Filters */}
            <div className="filters-section">
           
                    <div className="filters-bar">
                        {/* Top Row: Search, Date Range, View Controls */}
                        <div className="search-and-controls-row">
                            <div className="media-search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search content..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Inline Media Type Filters */}
                            <div className="media-type-filter-group inline">
                                <button 
                                    className={`media-type-btn small ${selectedMediaType === 'all' ? 'active' : ''}`}
                                    onClick={() => setSelectedMediaType('all')}
                                    title="All Media Types"
                                >
                                    <i className="fas fa-th-large"></i>
                                </button>
                                <button 
                                    className={`media-type-btn small ${selectedMediaType.toLowerCase() === 'video' ? 'active' : ''}`}
                                    onClick={() => setSelectedMediaType('video')}
                                    title="Videos Only"
                                >
                                    <i className="fas fa-video"></i>
                                </button>
                                <button 
                                    className={`media-type-btn small ${selectedMediaType === 'image' ? 'active' : ''}`}
                                    onClick={() => setSelectedMediaType('image')}
                                    title="Images Only"
                                >
                                    <i className="fas fa-image"></i>
                                </button>
                                <button 
                                    className={`media-type-btn small ${selectedMediaType === 'audio' ? 'active' : ''}`}
                                    onClick={() => setSelectedMediaType('audio')}
                                    title="Audio Only"
                                >
                                    <i className="fas fa-microphone"></i>
                                </button>
                            </div>

                            <div className="view-controls">
                                <button 
                                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <i className="fas fa-th"></i>
                                </button>
                                <button 
                                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => setViewMode('list')}
                                >
                                    <i className="fas fa-list"></i>
                                </button>
                            </div>
                        </div>

                        {/* Bottom Row: Filter Dropdowns */}
                        <div className="filter-controls">
                            {/* Date Range Filter - inline with other filters */}
                            <button 
                                className={`date-picker-button filter-select ${dateFrom && dateTo ? 'has-dates' : ''}`}
                                onClick={() => {
                                    setShowDatePicker(!showDatePicker);
                                    setTempStartDate(dateFrom);
                                    setTempEndDate(dateTo);
                                }}
                            >
                                <i className="fa fa-calendar"></i>
                                {dateFrom && dateTo ? (
                                    <span className="date-range-text">
                                        {formatDate(dateFrom)} - {formatDate(dateTo)}
                                    </span>
                                ) : (
                                    <span className="date-range-text">Select Dates</span>
                                )}
                            </button>
                            
                            {showDatePicker && (
                                <div className="date-range-filter" style={{position: 'relative', display: 'inline-block'}}>
                                    <div className="date-picker-overlay" onClick={() => setShowDatePicker(false)}></div>
                                        <div className="date-picker-popup">
                                        <div className="date-picker-calendars">
                                            <div className="calendar-container">
                                                <div className="calendar-header">Start Date</div>
                                                <DatePicker
                                                    selected={tempStartDate}
                                                    onChange={(date) => setTempStartDate(date)}
                                                    selectsStart
                                                    startDate={tempStartDate}
                                                    endDate={tempEndDate}
                                                    maxDate={new Date()}
                                                    inline
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    dateFormat="dd/MM/yyyy"
                                                    calendarClassName="custom-calendar"
                                                    renderCustomHeader={({
                                                        date,
                                                        changeYear,
                                                        changeMonth,
                                                        decreaseMonth,
                                                        increaseMonth,
                                                        prevMonthButtonDisabled,
                                                        nextMonthButtonDisabled,
                                                    }) => {
                                                        const currentDate = new Date();
                                                        const currentYear = currentDate.getFullYear();
                                                        const currentMonth = currentDate.getMonth();
                                                        
                                                        const availableYears = [...availableYearsAndMonths.keys()]
                                                            .sort((a, b) => b - a)
                                                            .filter(year => year <= currentYear);

                                                        const availableMonths = availableYearsAndMonths.get(date.getFullYear()) || new Set();
                                                        
                                                        const shouldDisableMonth = (monthIndex) => {
                                                            const yearToCheck = date.getFullYear();
                                                            if (yearToCheck > currentYear) return true;
                                                            if (yearToCheck === currentYear && monthIndex > currentMonth) return true;
                                                            return !availableMonths.has(monthIndex);
                                                        };
                                                        
                                                        return (
                                                            <div className="custom-header">
                                                                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                                                    {"<"}
                                                                </button>
                                                                <select
                                                                    value={date.getFullYear()}
                                                                    onChange={({ target: { value } }) => {
                                                                        const newYear = parseInt(value);
                                                                        if (newYear <= currentYear) {
                                                                            changeYear(newYear);
                                                                        }
                                                                    }}
                                                                >
                                                                    {availableYears.map(year => (
                                                                        <option 
                                                                            key={year} 
                                                                            value={year}
                                                                            disabled={year > currentYear}
                                                                        >
                                                                            {year}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                <select
                                                                    value={date.getMonth()}
                                                                    onChange={({ target: { value } }) => {
                                                                        const newMonth = parseInt(value);
                                                                        if (!shouldDisableMonth(newMonth)) {
                                                                            changeMonth(newMonth);
                                                                        }
                                                                    }}
                                                                >
                                                                    {monthNames.map((option, i) => (
                                                                        <option 
                                                                            key={i} 
                                                                            value={i}
                                                                            disabled={shouldDisableMonth(i)}
                                                                        >
                                                                            {option}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                <button 
                                                                    onClick={increaseMonth} 
                                                                    disabled={nextMonthButtonDisabled || shouldDisableMonth(date.getMonth() + 1)}
                                                                >
                                                                    {">"}
                                                                </button>
                                                            </div>
                                                        )}}
                                                />
                                                <div className="calendar-footer">
                                                    {tempStartDate ? formatDate(tempStartDate) : "No date selected"}
                                                </div>
                                            </div>
                                            <div className="calendar-container">
                                                <div className="calendar-header">End Date</div>
                                                <DatePicker
                                                    selected={tempEndDate}
                                                    onChange={(date) => setTempEndDate(date)}
                                                    selectsEnd
                                                    startDate={tempStartDate}
                                                    endDate={tempEndDate}
                                                    minDate={tempStartDate}
                                                    maxDate={new Date()}
                                                    inline
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    dateFormat="dd/MM/yyyy"
                                                    calendarClassName="custom-calendar"
                                                    renderCustomHeader={({
                                                        date,
                                                        changeYear,
                                                        changeMonth,
                                                        decreaseMonth,
                                                        increaseMonth,
                                                        prevMonthButtonDisabled,
                                                        nextMonthButtonDisabled,
                                                    }) => {
                                                        const currentDate = new Date();
                                                        const currentYear = currentDate.getFullYear();
                                                        const currentMonth = currentDate.getMonth();
                                                        
                                                        const availableYears = [...availableYearsAndMonths.keys()]
                                                            .sort((a, b) => b - a)
                                                            .filter(year => year <= currentYear);

                                                        const availableMonths = availableYearsAndMonths.get(date.getFullYear()) || new Set();
                                                        
                                                        const shouldDisableMonth = (monthIndex) => {
                                                            const yearToCheck = date.getFullYear();
                                                            if (yearToCheck > currentYear) return true;
                                                            if (yearToCheck === currentYear && monthIndex > currentMonth) return true;
                                                            return !availableMonths.has(monthIndex);
                                                        };
                                                        
                                                        return (
                                                            <div className="custom-header">
                                                                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                                                    {"<"}
                                                                </button>
                                                                <select
                                                                    value={date.getFullYear()}
                                                                    onChange={({ target: { value } }) => {
                                                                        const newYear = parseInt(value);
                                                                        if (newYear <= currentYear) {
                                                                            changeYear(newYear);
                                                                        }
                                                                    }}
                                                                >
                                                                    {availableYears.map(year => (
                                                                        <option 
                                                                            key={year} 
                                                                            value={year}
                                                                            disabled={year > currentYear}
                                                                        >
                                                                            {year}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                <select
                                                                    value={date.getMonth()}
                                                                    onChange={({ target: { value } }) => {
                                                                        const newMonth = parseInt(value);
                                                                        if (!shouldDisableMonth(newMonth)) {
                                                                            changeMonth(newMonth);
                                                                        }
                                                                    }}
                                                                >
                                                                    {monthNames.map((option, i) => (
                                                                        <option 
                                                                            key={i} 
                                                                            value={i}
                                                                            disabled={shouldDisableMonth(i)}
                                                                        >
                                                                            {option}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                <button 
                                                                    onClick={increaseMonth} 
                                                                    disabled={nextMonthButtonDisabled || shouldDisableMonth(date.getMonth() + 1)}
                                                                >
                                                                    {">"}
                                                                </button>
                                                            </div>
                                                        )}}
                                                />
                                                <div className="calendar-footer">
                                                    {tempEndDate ? formatDate(tempEndDate) : "No date selected"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="date-picker-actions">
                                            {(tempStartDate || tempEndDate) && (
                                                <button 
                                                    className="date-picker-clear" 
                                                    onClick={() => {
                                                        setTempStartDate(null);
                                                        setTempEndDate(null);
                                                        setDateFrom(null);
                                                        setDateTo(null);
                                                        setShowDatePicker(false);
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                            <div className="date-picker-actions-right">
                                                <button 
                                                    className="date-picker-cancel" 
                                                    onClick={() => setShowDatePicker(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button 
                                                    className="date-picker-apply"
                                                    onClick={handleApplyDates}
                                                    disabled={!tempStartDate || !tempEndDate}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <select 
                                value={selectedSeries} 
                                onChange={(e) => handleSmartFilterChange('series', e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Series</option>
                                {getDynamicFilterOptions.series.map(series => (
                                    <option key={series} value={series}>{series}</option>
                                ))}
                            </select>

                            <select 
                                value={selectedTopic} 
                                onChange={(e) => handleSmartFilterChange('topic', e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Topics</option>
                                {getDynamicFilterOptions.topics.map(topic => (
                                    <option key={topic} value={topic}>{topic}</option>
                                ))}
                            </select>

                            <select 
                                value={selectedProduct} 
                                onChange={(e) => handleSmartFilterChange('product', e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Products</option>
                                {getDynamicFilterOptions.products.map(product => (
                                    <option key={product} value={product}>{product}</option>
                                ))}
                            </select>

                            <select 
                                value={selectedTheme} 
                                onChange={(e) => handleSmartFilterChange('theme', e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Themes</option>
                                {getDynamicFilterOptions.themes.map(theme => (
                                    <option key={theme} value={theme}>{theme}</option>
                                ))}
                            </select>

                            <select 
                                value={selectedEntity} 
                                onChange={(e) => handleSmartFilterChange('entity', e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">All Entities</option>
                                {getDynamicFilterOptions.entities.map(entity => (
                                    <option key={entity} value={entity}>{entity}</option>
                                ))}
                            </select>

                            {hasActiveFilters && (
                                <button 
                                    className="view-btn action-btn view-all-btn"
                                    onClick={handleResetFilters}
                                    title="Clear all filters and view all content"
                                >
                                    <i className="fas fa-times-circle"></i>
                                </button>
                            )}
                        </div>
                    
                </div>
            </div>

            {/* Applied Filters Tags & Status Panel */}
            <div className="filters-status-section">
                <div className="container">
                    <div className="applied-filters-section">
                        {getAppliedFilters().length > 0 && (
                            <div className="applied-filters">
                                <span className="applied-filters-label">Applied filters:</span>
                                <div className="filter-tags">
                                    {getAppliedFilters().map((filter, index) => (
                                        <div key={`${filter.type}-${index}`} className="filter-tag">
                                            <span className="filter-tag-label">{filter.label}:</span>
                                            <span className="filter-tag-value">{filter.value}</span>
                                            <button 
                                                className="filter-tag-remove"
                                                onClick={filter.onRemove}
                                                title={`Remove ${filter.label} filter`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className="instant-stats-panel">
                            <div className="stats-left">
                                <div className="stats-description">
                                    Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)} of {totalItems} items
                                </div>
                                {totalItems < allMediaData.length && (
                                    <div className="stats-breakdown">
                                        <i className="fas fa-filter"></i>
                                        Filtered from {allMediaData.length.toLocaleString()} total
                                    </div>
                                )}
                            </div>
                            {!loading && (
                                <div className="stats-right">
                                    <div className="page-size-controls-inline">
                                        <label>Show: </label>
                                        <select value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}>
                                            <option value={6}>6 per page</option>
                                            <option value={12}>12 per page</option>
                                            <option value={24}>24 per page</option>
                                            <option value={48}>48 per page</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Grid */}
            <div className="content-section">
                <div className="container">

                    {mediaData.length === 0 && !loading ? (
                        <div className="no-results">
                            <i className="fas fa-search"></i>
                            <h3>No content found</h3>
                        </div>
                    ) : (
                        <>
                            {isLoadingPage && (
                                <div className="page-loading">
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>Loading page {loadingPageNumber}...</span>
                                </div>
                            )}
                            <div className={`content-grid ${viewMode} ${isLoadingPage ? 'loading' : ''}`}>
                                {mediaData.map((item, index) => (
                                <div 
                                    key={item.id}
                                    className={`media-card media-card-${item.mediaType.toLowerCase()}`}
                                    onClick={(e) => handleMediaClick(item, e)}
                                >
                                    <div className="media-thumbnail">
                                        {/* Priority 1: Check if we have a fetched thumbnail from videoThumbnails map */}
                                        {videoThumbnails[item.webURLLink] ? (
                                            // Show fetched thumbnail (external or custom blob thumbnail) for both videos and audio
                                            <>
                                        <img 
                                                    src={videoThumbnails[item.webURLLink]} 
                                            alt={item.topic}
                                            onError={(e) => {
                                                    e.target.style.display = 'none';
                                                        const placeholder = e.target.parentNode.querySelector('.thumbnail-placeholder');
                                                        if (placeholder) {
                                                            placeholder.style.display = 'flex';
                                                }
                                                    }}
                                                />
                                                <div className="thumbnail-placeholder" style={{display: 'none'}}>
                                                    <i className={getMediaTypeIcon(item.mediaType)}></i>
                                                    <span>{getMediaTypeDisplay(item.mediaType)}</span>
                                                </div>
                                            </>
                                        ) : item.mediaType.toLowerCase() === 'video' && 
                                         !item.webURLLink.includes('vimeo.com') && 
                                         !item.webURLLink.includes('youtube.com') && 
                                         !item.webURLLink.includes('youtu.be') ? (
                                            // Show local video placeholder for videos without custom thumbnails
                                            <div className="thumbnail-placeholder local-video-thumb">
                                                <div className="video-icon-wrapper">
                                                    <i className="fas fa-play-circle"></i>
                                                </div>
                                                <div className="video-title-overlay">
                                                    <span>{item.topic}</span>
                                                </div>
                                            </div>
                                        ) : (item.mediaType.toLowerCase() === 'audio' || item.mediaType === 'Audio') ? (
                                            // Show audio placeholder for audio files without thumbnails
                                            <div className="thumbnail-placeholder local-audio-thumb">
                                                <div className="audio-icon-wrapper">
                                                    <i className="icon-volume-2" style={{ fontSize: '2rem' }}></i>
                                                </div>
                                                <div className="audio-title">{item.topic}</div>
                                            </div>
                                        ) : item.mediaType.toLowerCase() === 'image' ? (
                                            // Show the actual image for image media types
                                            <>
                                                {imageUrls[item.webURLLink] ? (
                                                    <>
                                                        <img 
                                                            src={imageUrls[item.webURLLink]} 
                                                            alt={item.topic}
                                                            className="image-thumbnail"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const placeholder = e.target.parentNode.querySelector('.thumbnail-placeholder');
                                                                if (placeholder) {
                                                                    placeholder.style.display = 'flex';
                                                                }
                                                            }}
                                                        />
                                                        <div className="image-overlay">
                                                            <i className="fas fa-expand"></i>
                                                        </div>
                                                    </>
                                                ) : (
                                                    // Show loading placeholder while image loads
                                                    <div className="thumbnail-placeholder image-loading">
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                        <span>Loading...</span>
                                                    </div>
                                                )}
                                                <div className="thumbnail-placeholder image-placeholder" style={{display: 'none'}}>
                                                    <i className="fas fa-image"></i>
                                                    <span>Image</span>
                                                </div>
                                            </>
                                        ) : areThumbnailsLoading && (item.mediaType.toLowerCase() === 'video' && 
                                             (item.webURLLink.includes('vimeo.com') || item.webURLLink.includes('youtube.com') || item.webURLLink.includes('youtu.be'))) ? (
                                            // Show loading placeholder for YouTube/Vimeo videos while thumbnails are being fetched
                                            <div className="thumbnail-placeholder" style={{background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'}}>
                                                <i className="fas fa-spinner fa-spin"></i>
                                                <span>Loading thumbnail...</span>
                                            </div>
                                        ) : (
                                            // Fallback for other media types or if thumbnail loading fails
                                            <>
                                                <img 
                                                    src={item.vimeoThumbnail || item.thumbnail || '/assets/img/media/default-thumb.jpg'} 
                                                    alt={item.topic}
                                                    onError={(e) => {
                                                // Try Vimeo thumbnail if manual thumbnail fails
                                                if (item.vimeoThumbnail && e.target.src !== item.vimeoThumbnail) {
                                                    e.target.src = item.vimeoThumbnail;
                                                } else {
                                                    e.target.style.display = 'none';
                                                    const placeholder = e.target.parentNode.querySelector('.thumbnail-placeholder');
                                                    if (placeholder) {
                                                        placeholder.style.display = 'flex';
                                                    }
                                                }
                                            }}
                                        />
                                            <div className="thumbnail-placeholder" style={{display: 'none'}}>
                                                <i className={getMediaTypeIcon(item.mediaType)}></i>
                                                <span>{getMediaTypeDisplay(item.mediaType)}</span>
                                            </div>
                                            </>
                                        )}
                                        {showPlayOverlay(item.mediaType) && (
                                            <div className="play-overlay">
                                                <i className={`fas ${(item.mediaType.toLowerCase() === 'audio' || item.mediaType === 'Audio') ? 'fa-headphones' : 'fa-play-circle'}`}></i>
                                            </div>
                                        )}
                                        

                                        {showDuration(item.mediaType) && item.duration && (
                                            <div className="duration-badge">{item.duration}</div>
                                        )}
                                        <div className="media-type-badge">
                                            <i className={getMediaTypeIcon(item.mediaType)}></i>
                                            <span>{getMediaTypeDisplay(item.mediaType)}</span>
                                        </div>
                                    </div>

                                    <div className="media-info">
                                        <h3 className="media-title" title={item.topic}>{item.topic}</h3>
                                        <p className="media-description">{item.shortDescription}</p>

                                        <div className="media-tags">
                                            {item.series && item.series.length > 0 && item.series
                                                .filter(series => series && series.trim() !== '') // Filter out empty/null series
                                                .map(series => (
                                                    <span key={series} className={`tag series-tag`}>
                                                        {series}
                                                    </span>
                                                ))
                                            }
                                            {item.productService && item.productService.trim() !== '' && (
                                                <span className={`tag product-tag tag-${getProductColor(item.productService)}`}>
                                                    {item.productService}
                                                </span>
                                            )}
                                            {item.theme && item.theme.trim() !== '' && (
                                                <span className={`tag theme-tag tag-${getThemeColor(item.theme)}`}>
                                                    {item.theme}
                                                </span>
                                            )}
                                        </div>

                                        <div className="media-meta">
                                            <span className="publish-date">
                                                <i className="fas fa-calendar"></i>
                                                {new Date(item.publishDate).toLocaleDateString('en-GB', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </span>
                                            <span className="entity">
                                                <i className="fas fa-building"></i>
                                                {item.entity}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            </div>
                            
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination">
                                    <div className="pagination-info">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <div className="pagination-controls">
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fas fa-angle-double-left"></i>
                                        </button>
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fas fa-angle-left"></i>
                                        </button>
                                        
                                        {/* Page numbers */}
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fas fa-angle-right"></i>
                                        </button>
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fas fa-angle-double-right"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            
            {/* Video Modal */}
            {showVideoModal && selectedVideo && (
                <div className="video-modal-overlay" onClick={closeVideoModal}>
                    <div className="video-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="video-modal-header">
                            <h3>{selectedVideo.topic || selectedVideo.title}</h3>
                            <div className="video-modal-controls">
                                <button className="fullscreen-btn" onClick={toggleVideoFullscreen} title="Toggle Fullscreen">
                                    <i className="fas fa-expand"></i>
                                    <i className="fas fa-compress"></i>
                                </button>
                                <button className="close-btn" onClick={closeVideoModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                        <div className="video-modal-content">
                            {/* Handle Vimeo/YouTube videos for external URLs */}
                            {selectedVideo.webURLLink && (
                                selectedVideo.webURLLink.includes('vimeo.com') || 
                                selectedVideo.webURLLink.includes('youtube.com') || 
                                selectedVideo.webURLLink.includes('youtu.be')
                            ) ? (
                                <iframe
                                    src={selectedVideo.webURLLink.includes('vimeo.com') 
                                        ? getVimeoEmbedUrl(selectedVideo.webURLLink)
                                        : getEmbeddableVideoUrl(selectedVideo.webURLLink)
                                    }
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    title={selectedVideo.topic || selectedVideo.title}
                                ></iframe>
                            ) : (
                                /* Handle local blob videos with proper loading */
                                <>
                                    {isVideoLoading ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <LoadingSpinner />
                                        </div>
                                    ) : videoSource ? (
                                        isVideoEmbedded ? (
                                            <iframe
                                                src={videoSource}
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                                                 allowFullScreen
                                                 title={selectedVideo.topic || selectedVideo.title}
                                            />
                            ) : (
                                <video
                                    width="100%"
                                    height="100%"
                                    controls
                                    autoPlay
                                    src={videoSource}
                                >
                                    Your browser does not support the video tag.
                                </video>
                                        )
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#666' }}>
                                            <p>Failed to load video</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="video-modal-info">
                            <div className="video-modal-meta media-meta">
                                {selectedVideo.duration && (
                                    <span><i className="fas fa-clock"></i> {selectedVideo.duration}</span>
                                )}
                                <span><i className="fas fa-calendar"></i> {new Date(selectedVideo.publishDate).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                })}</span>
                                <span className='entity'><i className="fas fa-building"></i> {selectedVideo.entity}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {showImageModal && selectedImage && (
                <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
                    <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="image-modal-header">
                            <h3>
                                <span>{selectedImage.topic.length > 50 ? `${selectedImage.topic.substring(0, 50)}...` : selectedImage.topic}</span>
                            </h3>
                            <button 
                                className="image-modal-close"
                                onClick={() => setShowImageModal(false)}
                                title="Close image"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="image-modal-content">
                            <img src={imageUrls[selectedImage.webURLLink] || selectedImage.webURLLink} alt={selectedImage.topic} />
                        </div>
                        <div className="image-modal-info">
                            <div className="image-modal-meta media-meta">
                                <span><i className="fas fa-calendar"></i> {new Date(selectedImage.publishDate).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                })}</span>
                                <span className='entity'><i className="fas fa-building"></i> {selectedImage.entity}</span>
                                {selectedImage.shortDescription && (
                                    <div className="image-description">
                                        <p>{selectedImage.shortDescription}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MediaCentreComponent; 